import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Building2, ArrowLeft, Send } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useForceLightMode } from "../hooks/useForceLightMode";

const RegisterLDPage = () => {
  useForceLightMode();
  const [lds, setLds] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    ld_koda: "",
    predsednik_ime: "",
    predsednik_priimek: "",
    predsednik_lovska_izkaznica: "",
    predsednik_stevilka_clana: "",
    predsednik_email: "",
    predsednik_telefon: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedLd, setSelectedLd] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/lovske-druzine/directory`);
        setLds((r.data || []).filter(ld => ld.status === "lzs_registry" || ld.status === undefined || ld.aktivna === false));
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Track invitation click (?inv=xxx) + apply prefill from URL
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const invId = params.get("inv");
      const prefill = params.get("prefill");
      if (invId && invId !== "preview") {
        axios.post(`${API}/invitations/track-click?inv_id=${encodeURIComponent(invId)}`).catch(() => {});
      }
      if (prefill && !form.ld_koda) {
        setForm(f => ({ ...f, ld_koda: prefill }));
      }
    } catch (_) { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLds = search.length >= 2
    ? lds.filter(ld => ld.naziv.toLowerCase().includes(search.toLowerCase())).slice(0, 100)
    : lds.slice(0, 100);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ld_koda) return toast.error("Izberi svojo LD iz seznama");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/lovske-druzine/register`, form);
      toast.success(r.data.message);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Napaka");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen login-bg flex items-center justify-center p-4">
        <Card className="glass border-0 shadow-2xl max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <Send className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Registracija oddana</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Prošnja za <strong>{selectedLd?.naziv}</strong> je bila oddana super-adminu v pregled.
              Ko bo LD aktivirana, se boste lahko prijavili z vašo izkaznico ({form.predsednik_lovska_izkaznica}).
            </p>
            <Button onClick={() => navigate("/login")} className="w-full" data-testid="back-to-login">
              <ArrowLeft className="h-4 w-4 mr-2" />Nazaj na prijavo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-2xl">
        <Card className="glass border-0 shadow-2xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src="/dober-pogled-logo.png" alt="" className="w-12 h-12 rounded-full" />
              <div>
                <CardTitle className="text-2xl">Registracija lovske družine</CardTitle>
                <CardDescription>Izberi svojo LD iz LZS seznama in vpiši podatke prvega predsednika.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* LD selection */}
              <div className="space-y-2">
                <Label>Vaša lovska družina (po LZS) *</Label>
                <Input placeholder="Poišči po imenu (npr. Rakek, Cerknica…)" 
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  data-testid="ld-search" />
                <Select value={form.ld_koda} 
                  onValueChange={(v) => { 
                    upd("ld_koda", v); 
                    setSelectedLd(lds.find(l => l.koda === v));
                  }}>
                  <SelectTrigger data-testid="reg-ld-select">
                    <SelectValue placeholder={`Izberi LD (${filteredLds.length} rezultatov)`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-96">
                    {filteredLds.map(ld => (
                      <SelectItem key={ld.koda} value={ld.koda} data-testid={`reg-ld-${ld.koda}`}>
                        {ld.naziv} {ld.posta ? `– ${ld.posta}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* President info */}
              <div className="space-y-3 border-t pt-4">
                <h3 className="text-sm font-semibold text-primary uppercase">Prvi predsednik (dostop)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Ime *</Label><Input required value={form.predsednik_ime} onChange={(e) => upd("predsednik_ime", e.target.value)} data-testid="reg-pred-ime" /></div>
                  <div className="space-y-1"><Label>Priimek *</Label><Input required value={form.predsednik_priimek} onChange={(e) => upd("predsednik_priimek", e.target.value)} data-testid="reg-pred-priimek" /></div>
                  <div className="space-y-1"><Label>Št. lovske izkaznice *</Label><Input required value={form.predsednik_lovska_izkaznica} onChange={(e) => upd("predsednik_lovska_izkaznica", e.target.value)} data-testid="reg-pred-liz" /></div>
                  <div className="space-y-1"><Label>Št. člana (geslo) *</Label><Input required value={form.predsednik_stevilka_clana} onChange={(e) => upd("predsednik_stevilka_clana", e.target.value)} data-testid="reg-pred-num" /></div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label>E-mail <span className="text-primary">*</span></Label>
                    <Input
                      required
                      type="email"
                      value={form.predsednik_email}
                      onChange={(e) => upd("predsednik_email", e.target.value)}
                      placeholder="predsednik@lovskazveza.si"
                      data-testid="reg-pred-email"
                    />
                    <div className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-md p-2 leading-relaxed">
                      <b>⚠️ Priporočamo uradni e-mail LD-ja</b> (npr. <code>@lovskazveza.si</code> ali domenski e-mail vaše družine iz LZS registra).
                      Če se e-mail ujema s tistim, ki ga imamo v LZS bazi, se vaša LD <b>samodejno aktivira</b> in ne rabite čakati na potrditev.
                      Sicer bomo prošnjo ročno pregledali (do 24 ur).
                    </div>
                  </div>
                  <div className="space-y-1 sm:col-span-2"><Label>Telefon</Label><Input value={form.predsednik_telefon} onChange={(e) => upd("predsednik_telefon", e.target.value)} /></div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Link to="/login" className="flex-1">
                  <Button type="button" variant="outline" className="w-full"><ArrowLeft className="h-4 w-4 mr-2" />Prekliči</Button>
                </Link>
                <Button type="submit" className="flex-1" disabled={loading} data-testid="reg-submit-btn">
                  {loading ? <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" /> : (<><Send className="h-4 w-4 mr-2" />Oddaj registracijo</>)}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterLDPage;
